/**
 * Корневой компонент приложения.
 *
 * Настраивает маршрутизацию и распределяет доступ по ролям:
 * - /login, /register — публичные страницы
 * - /patient/* — доступны только пациентам
 * - /doctor/* — доступны только врачам
 *
 * Главная страница (/) перенаправляет на дашборд в зависимости от роли.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import Dashboard from './pages/patient/Dashboard';
import DiaryPage from './pages/patient/DiaryPage';
import ProfilePage from './pages/patient/ProfilePage';
import RelapsesPage from './pages/patient/RelapsesPage';
import TestsPage from './pages/tests/TestsPage';
import DoctorDashboardPage from './pages/doctor/DashboardPage';
import PatientsListPage from './pages/doctor/PatientsListPage';
import PatientDetailPage from './pages/doctor/PatientDetailPage';
import NotificationsPage from './pages/doctor/NotificationsPage';
import AuditLogPage from './pages/doctor/AuditLogPage';
import SettingsPage from './pages/shared/SettingsPage';
import MessagesPage from './pages/shared/MessagesPage';
import AboutPage from './pages/shared/AboutPage';

/**
 * Компонент главной страницы.
 * Перенаправляет на нужный раздел в зависимости от роли пользователя.
 */
const HomePage: React.FC = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'patient') return <Navigate to="/patient/dashboard" replace />;
  if (user.role === 'doctor') return <Navigate to="/doctor/dashboard" replace />;

  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Публичные маршруты (без Layout) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Защищённые маршруты (с Layout) */}
          <Route path="/" element={
            <Layout>
              <HomePage />
            </Layout>
          } />

          {/* Маршруты пациента */}
          <Route path="/patient/dashboard" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <Dashboard />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/patient/diary" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <DiaryPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/patient/tests" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <TestsPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/patient/profile" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <ProfilePage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/patient/relapses" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <RelapsesPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/patient/notifications" element={
            <Layout>
              <ProtectedRoute requiredRole="patient">
                <NotificationsPage />
              </ProtectedRoute>
            </Layout>
          } />

          {/* Маршруты врача */}
          <Route path="/doctor/dashboard" element={
            <Layout>
              <ProtectedRoute requiredRole="doctor">
                <DoctorDashboardPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/doctor/patients" element={
            <Layout>
              <ProtectedRoute requiredRole="doctor">
                <PatientsListPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/doctor/patients/:patientId" element={
            <Layout>
              <ProtectedRoute requiredRole="doctor">
                <PatientDetailPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/doctor/notifications" element={
            <Layout>
              <ProtectedRoute requiredRole="doctor">
                <NotificationsPage />
              </ProtectedRoute>
            </Layout>
          } />
          <Route path="/doctor/audit" element={
            <Layout>
              <ProtectedRoute requiredRole="doctor">
                <AuditLogPage />
              </ProtectedRoute>
            </Layout>
          } />
          {/* Общие маршруты */}
          <Route path="/messages" element={
            <Layout>
              <MessagesPage />
            </Layout>
          } />
          <Route path="/settings" element={
            <Layout>
              <SettingsPage />
            </Layout>
          } />
          <Route path="/about" element={
            <Layout>
              <AboutPage />
            </Layout>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
